import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";

/**
 * The editor's own chrome, transcribed from `Trioty Restaurant Editor.dc.html`.
 *
 * It does NOT use the portal's `--background` / `--card` tokens. The editor is
 * a workbench wrapped around a picture of the seller's storefront, and the two
 * have to stay visually apart: a panel painted in the same greys as the page it
 * is previewing reads as part of that page. The artboard sets its own scale —
 * a 52px bar, a 290px rail, 9–12px radii, an almost-black accent — and these
 * are those values, scoped to one root so nothing leaks into the rest of the
 * portal.
 */
export const EDITOR_VARS = `
.tri-root{
--app-bg:#E6E6E4;--shell:#fff;--shell-br:#e3e3e3;--hair:#F0F0F0;--card:#fff;--card-br:#ECECEC;
--ink:#1A1A19;--ink2:#5B5B55;--ink3:#8C8C86;--ink4:#A6A6A0;
--soft:#fafafa;--soft2:#f5f5f5;--hover:#efefef;--pill:#f0f0f0;--pillring:#e6e6e6;
--chip:#F4F4F2;--chipbr:#ececec;--accent:#1A1A19;--accent-ink:#fff;--accent-hover:#2C2C29;
--ok-fg:#1F8A4C;--input:#fafafa;--input-br:#e5e5e5;
}
.tri-root ::-webkit-scrollbar{width:10px;height:10px}
.tri-root ::-webkit-scrollbar-thumb{background:var(--pillring);border-radius:9px;border:3px solid transparent;background-clip:content-box}
@keyframes triRise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
.tri-ib{transition:background .12s,color .12s}
.tri-ib:hover:not(:disabled){background:var(--hover) !important;color:var(--ink) !important}
.tri-ib:disabled{opacity:.4;cursor:not-allowed}
.ed-row{transition:background .12s}.ed-row:hover{background:var(--soft) !important}
.ed-theme{transition:border-color .13s,transform .13s}.ed-theme:hover{transform:translateY(-1px)}
.ed-publish{transition:background .13s,transform .1s}
.ed-publish:hover:not(:disabled){background:var(--accent-hover) !important}
.ed-publish:active:not(:disabled){transform:translateY(1px)}
.ed-publish:disabled{opacity:.45;cursor:not-allowed}
.tri-root input,.tri-root textarea{outline:none;font-family:inherit}
@media (prefers-reduced-motion:reduce){
.tri-ib,.ed-row,.ed-theme,.ed-publish{transition:none}
.ed-theme:hover,.ed-publish:active{transform:none}
}
`;

/** A small letterspaced group heading — the artboard's `THEME` / `HERO`. */
export function GroupLabel({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: ".07em",
        color: "var(--ink4)",
        marginBottom: 8,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * A labelled setting, edited in BOTH languages.
 *
 * The storefront is bilingual, so a control that edits one language is a
 * control that quietly lets half the page rot: a seller rewrites the English
 * hero, the Arabic one keeps the words the theme shipped with, and nothing on
 * the screen says so. Both boxes are always visible for the same reason the
 * copy editor before it showed both — an accordion or a language switch hides
 * exactly the half that gets forgotten.
 *
 * Each box reports every keystroke. There is no save button in this editor: the
 * page holds the newest draft and flushes it once the seller pauses, so the
 * field's job is to be current, not to decide when a write happens. It keeps a
 * LOCAL value so the caret does not jump when the stored value arrives a moment
 * later, from a request the seller has already typed past.
 */
export function Field({
  label,
  ar,
  en,
  onChange,
  disabled,
  testId,
}: {
  label: string;
  ar: string;
  en: string;
  onChange: (locale: "ar" | "en", next: string) => void;
  disabled?: boolean;
  testId?: string;
}) {
  const [draft, setDraft] = useState({ ar, en });
  const row = (locale: "ar" | "en") => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        aria-hidden="true"
        style={{
          width: 22,
          flexShrink: 0,
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: ".06em",
          color: "var(--ink4)",
          textTransform: "uppercase",
        }}
      >
        {locale}
      </span>
      {/* eslint-disable-next-line no-restricted-syntax -- the editor is drawn to
          its own artboard scale (33px, 9px radius, its own ink ramp); the
          platform TextInput would drag the portal's form styling into a
          workbench that is deliberately a different surface. */}
      <input
        type="text"
        value={draft[locale]}
        /* Each box is typed in its OWN script regardless of the portal's
           language — an Arabic line entered into a left-to-right box reorders
           as you type and is unreadable. */
        dir={locale === "ar" ? "rtl" : "ltr"}
        lang={locale}
        disabled={disabled}
        aria-label={`${label} — ${locale.toUpperCase()}`}
        onChange={(event) => {
          const next = event.target.value;
          setDraft((previous) => ({ ...previous, [locale]: next }));
          onChange(locale, next);
        }}
        data-testid={testId ? `${testId}-${locale}` : undefined}
        style={{
          width: "100%",
          minWidth: 0,
          height: 30,
          borderRadius: 9,
          background: "var(--input)",
          border: "1px solid var(--input-br)",
          padding: "0 9px",
          fontSize: 11.5,
          fontWeight: 700,
          color: "var(--ink)",
        }}
      />
    </div>
  );
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: "var(--ink2)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {row("en")}
        {row("ar")}
      </div>
    </div>
  );
}

/** The artboard's pill switch, used for section visibility. */
export function PillToggle({
  on,
  onToggle,
  label,
  disabled,
  testId,
}: {
  on: boolean;
  onToggle: () => void;
  label: string;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      data-testid={testId}
      style={{
        flexShrink: 0,
        width: 32,
        height: 19,
        borderRadius: 99,
        border: "none",
        background: on ? "var(--ok-fg)" : "var(--pillring)",
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative",
        transition: "background .15s",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          insetInlineStart: 2,
          width: 15,
          height: 15,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,.25)",
          transform: on ? "translateX(13px)" : "none",
          transition: "transform .15s",
        }}
      />
    </button>
  );
}

/** The open / struck-through eye on a section row. */
export function EyeIcon({ on }: { on: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {on ? (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
          <circle cx="12" cy="12" r="3" />
        </>
      ) : (
        <>
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 8 10 8a13.16 13.16 0 0 1-1.67 2.68" />
          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3.5 8 10 8a9.74 9.74 0 0 0 5.39-1.61" />
          <path d="m2 2 20 20" />
        </>
      )}
    </svg>
  );
}

/**
 * A 22px ghost icon button — the section row's move and visibility controls.
 *
 * NOT `@a2z/ui`'s `IconButton`, and deliberately not named as though it were.
 * That one starts at 28px and draws from the portal's `--text-muted` /
 * `--border-light` ramp; the artboard's row buttons are 22px on the editor's
 * own ink, three to a row inside a 290px rail, where four extra pixels each is
 * the difference between a row and a crowd. Adding a smaller size and a palette
 * override to the shared primitive would push one editor's needs into every
 * app that imports it.
 */
export function EditorIconButton({
  label,
  onClick,
  disabled,
  children,
  size = 22,
  testId,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
  size?: number;
  testId?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="tri-ib"
      style={{
        width: size,
        height: size,
        borderRadius: size > 22 ? 7 : 6,
        border: "none",
        background: "transparent",
        color: "var(--ink4)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}
